import React from 'react';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';

export type CustomTooltipProps = TooltipProps & { autoHideDuration?: number };

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    slotProps={{
      popper: {
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 0],
            },
          },
        ],
      },
    }}
    {...props}
    classes={{ popper: className }}
  />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    background: 'rgba(255, 255, 255, 0.95)',
    color: '#1a1a1a',
    padding: 5,
    borderRadius: 8,
    fontSize: 12,
    whiteSpace: 'pre-line',
    textAlign: 'left',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: '1px solid #e0e0e0',
    lineHeight: 1.4,
    backdropFilter: 'blur(10px)',
    maxWidth: 240,
    maxHeight: 'calc(100vh - 16px)',
    overflow: 'hidden',
    margin: '2px !important',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'rgba(255, 255, 255, 0.95)',
    '&::before': {
      border: '1px solid #e0e0e0',
    },
  },
}));

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  autoHideDuration = 1500,
  ...props
}) => {
  const [open, setOpen] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  };

  const handleOpen = () => {
    clearTimer();
    setOpen(true);
    timerRef.current = setTimeout(() => setOpen(false), autoHideDuration);
  };

  const handleClose = () => {
    clearTimer();
    setOpen(false);
  };

  React.useEffect(() => clearTimer, []);

  return (
    <StyledTooltip
      {...props}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      enterTouchDelay={0}
      disableFocusListener
    />
  );
};

export default CustomTooltip;
